import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { formatDisplayDate } from "@/lib/dateFormat";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  listEligibleRequests,
  sendRequestFeedbackNow,
  type EligibleRequestItem,
  type RequestFeedbackRequestType,
} from "@/services/requestFeedbackService";
import { requestTypeLabel } from "./request-type-label";

const ELIGIBLE_QUERY_KEY = ["platform", "request-feedback", "eligible"] as const;

interface SendSurveyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSent?: () => void;
}

export function SendSurveyDialog({
  open,
  onOpenChange,
  onSent,
}: SendSurveyDialogProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Only root may step over a running cadence; root_admin sees it locked.
  const isRoot = user?.platformRole === "root";
  // Forcing past a running cadence is confirmed inline rather than with a
  // native confirm(), which the project bans: it blocks the thread, can't be
  // styled, and would sit on top of this dialog.
  const [confirmingKey, setConfirmingKey] = useState<string | null>(null);

  const eligibleQuery = useQuery({
    queryKey: ELIGIBLE_QUERY_KEY,
    queryFn: () => listEligibleRequests(50),
    enabled: open,
  });

  const sendMutation = useMutation({
    mutationFn: (params: {
      requestType: RequestFeedbackRequestType;
      requestId: string;
      force?: boolean;
    }) => sendRequestFeedbackNow(params),
    onSuccess: (status) => {
      // Every outcome refetches: the row's attempt state changed in most of
      // them, and a stale badge after a click is the complaint that started
      // this rewrite.
      void queryClient.invalidateQueries({ queryKey: ELIGIBLE_QUERY_KEY });
      setConfirmingKey(null);
      switch (status) {
        case "sent":
          toast.success(t("ratings.send.toastSent"));
          onSent?.();
          break;
        case "already_submitted":
          toast.error(t("ratings.send.toastAlreadySubmitted"));
          break;
        case "no_consumer_email":
          toast.error(t("ratings.send.toastNoEmail"));
          break;
        case "send_failed":
          toast.error(t("ratings.send.toastSendFailed"));
          break;
        case "blocked_cadence_running":
          toast.error(t("ratings.send.toastCadenceRunning"));
          break;
        case "claimed_elsewhere":
          toast.error(t("ratings.send.toastClaimedElsewhere"));
          break;
      }
    },
    onError: () => {
      toast.error(t("ratings.send.toastError"));
    },
  });

  const items = eligibleQuery.data ?? [];
  const loading = eligibleQuery.isLoading;
  const pendingId =
    sendMutation.isPending && sendMutation.variables
      ? `${sendMutation.variables.requestType}:${sendMutation.variables.requestId}`
      : null;

  const typeLabel = (type: RequestFeedbackRequestType): string =>
    requestTypeLabel(t, type);

  const shortDate = (iso: string): string =>
    formatDisplayDate(iso, { includeTime: false, locale: i18n.language });

  /**
   * One place that turns a row's attempt state into what the operator sees:
   * badge text, badge tone, whether the button is live, and what it says.
   * Computed here rather than inline so the JSX stays a list of references.
   */
  const describe = (
    row: EligibleRequestItem,
  ): {
    badge: string;
    tone: string;
    note: string | null;
    canSend: boolean;
    forcing: boolean;
    actionLabel: string;
  } => {
    // The cadence owns the row until it is exhausted. Root can override that,
    // and the label says so, because "Reenviar" on a row with two reminders
    // still queued would not describe what the click actually does.
    const forcing = !row.canSendManually && isRoot;
    const base = {
      canSend: row.canSendManually || isRoot,
      forcing,
      actionLabel: forcing
        ? t("ratings.send.actionForce")
        : row.attemptCount > 0
          ? t("ratings.send.actionResend")
          : t("ratings.send.actionSend"),
      note: row.lastAttemptFailed ? t("ratings.send.lastAttemptFailed") : null,
    };
    switch (row.feedbackState) {
      case "exhausted":
        return {
          ...base,
          badge: t("ratings.send.stateExhausted", { count: row.attemptCount }),
          tone: "bg-muted text-muted-foreground",
        };
      case "in_cadence":
        return {
          ...base,
          badge: row.nextAttemptAt
            ? t("ratings.send.stateInCadence", {
                sent: row.attemptCount,
                total: row.totalAttempts,
                date: shortDate(row.nextAttemptAt),
              })
            : t("ratings.send.stateSentNoNext", { sent: row.attemptCount }),
          tone: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
        };
      case "scheduled":
        return {
          ...base,
          badge: row.nextAttemptAt
            ? t("ratings.send.stateScheduled", {
                date: shortDate(row.nextAttemptAt),
              })
            : t("ratings.send.stateScheduledUnknown"),
          tone: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
        };
      case "none":
      default:
        return {
          ...base,
          badge: t("ratings.send.stateNone"),
          tone: "bg-muted text-muted-foreground",
        };
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("ratings.send.title")}</DialogTitle>
          <DialogDescription>
            {t("ratings.send.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto rounded-md border border-border">
          {loading ? (
            <div className="flex min-h-[200px] items-center justify-center py-8">
              <LoadingLogo size="md" />
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              {t("ratings.send.empty")}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((row) => {
                const key = `${row.requestType}:${row.requestId}`;
                const isPending = pendingId === key;
                const state = describe(row);
                const isConfirming = confirmingKey === key;
                return (
                  <li key={key} className="flex items-start gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <p className="truncate font-medium text-foreground">
                          {row.consumerName?.trim() ||
                            row.consumerEmail ||
                            "—"}
                        </p>
                        {row.consumerName?.trim() && row.consumerEmail ? (
                          <span className="truncate text-xs text-muted-foreground">
                            {row.consumerEmail}
                          </span>
                        ) : null}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {typeLabel(row.requestType)} · {row.productLabel}
                        {row.professionalName
                          ? ` · ${row.professionalName}`
                          : ""}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t("ratings.send.requestedAt", {
                          date: formatDisplayDate(row.createdAt, {
                            includeTime: false,
                            locale: i18n.language,
                          }),
                        })}
                        <span
                          className={cn(
                            "ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium",
                            state.tone
                          )}
                        >
                          {state.badge}
                        </span>
                      </p>
                      {state.note ? (
                        <p className="text-xs font-medium text-destructive">
                          {state.note}
                        </p>
                      ) : null}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant={
                        isConfirming
                          ? "destructive"
                          : state.canSend
                            ? "default"
                            : "outline"
                      }
                      disabled={sendMutation.isPending || !state.canSend}
                      title={
                        state.canSend ? undefined : t("ratings.send.lockedHint")
                      }
                      onClick={() => {
                        // First click on a forced send arms it; second sends.
                        if (state.forcing && confirmingKey !== key) {
                          setConfirmingKey(key);
                          return;
                        }
                        sendMutation.mutate({
                          requestType: row.requestType,
                          requestId: row.requestId,
                          force: state.forcing,
                        });
                      }}
                    >
                      {isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="mr-2 h-4 w-4" />
                      )}
                      {isConfirming
                        ? t("ratings.send.forceConfirm")
                        : state.actionLabel}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("ratings.send.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
